import { trackedObject } from '@ember/reactive/collections';
import Service, { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export default class PricingAssistantPmsSsService extends Service {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare localStorage: Services['local-storage'];
  @service declare myotbSs: Services['myotb-ss'];

  // Tracked object to store the state of the PMS SS upsell banner when the user dismisses it
  // Why? When user dismisses a banner, we store this information in localStorage directly.
  // But making changes to the localStorage doesn't trigger reactivity and as a result UI doesn't update accordingly i.e. the banner doesn't disappear.
  // Therefore, we also update this information in a tracked object in order to trigger reactivity.
  bannerVisibilityStates = trackedObject<Record<string, boolean>>();

  // Task to launch a request to start the PMS connect flow
  // Added in this service so that we can always check if this task is running or not
  launchConnectPmsRequestTask = task({ drop: true }, async () => {
    try {
      const requestUrl = formatUrl(
        '/apigateway/v1/app/pricing-assistant/selfservice/pms/connect',
        {
          // @ts-expect-error: Type 'string | null' is not assignable to type 'string | number | boolean | string[] | number[]'.
          subscription_id: this.hotelAndMarketService.hotel.id,
        },
        this.ajax.host,
      );

      await this.ajax.post(requestUrl, {
        contentType: 'application/json',
      });

      return {
        isSuccess: true,
      };
    } catch (error) {
      return {
        isSuccess: false,
        error,
      };
    }
  });

  // Show OTB placeholders when OTB SS is available or validating
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get arePricingAssistantPMSSelfServiceOTBPlaceholdersVisible() {
    return (
      this.access.hasFeature('otb.empty_placeholder') ||
      this.access.hasFeature('otb.data_validation')
    );
  }

  // Show OTB upsell banner only when the PMS integration is not available but OTB is
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPricingAssistantOtbSelfServiceUpsellPrimaryBannerVisible() {
    return (
      !this.isPricingAssistantPMSSelfServiceAvailable &&
      !this.isPricingAssistantPMSSelfServiceInProgress &&
      this.myotbSs.isOtbSelfServiceUpsellBannerVisible
    );
  }

  // Show just the OTB button or progress pill in case user has dismissed the primary banner or the data validation is in progress
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPricingAssistantOtbSelfServiceUpsellSecondaryBannerVisible() {
    return (
      !this.isPricingAssistantPMSSelfServiceAvailable &&
      !this.isPricingAssistantPMSSelfServiceInProgress &&
      !this.myotbSs.isOtbSelfServiceUpsellBannerVisible &&
      (this.access.hasFeature('otb.empty_placeholder') ||
        this.access.hasFeature('otb.data_validation'))
    );
  }

  /*
    Pricing Assistant PMS signup FF explanation:
    pricing_assistant.signup.open -> User has not started the signup flow
    pricing_assistant.signup.in_progress -> User has started the signup flow
    pricing_assistant.pms.push_integrated -> PMS is integrated
    It's possible that a user has a PMS that we don't integrate with. In that case the user will never be able to push. They should never receive the pricing_assistant.signup.open FF.

    OTB self service FF explanation:
    otb.empty_placeholder -> OTB self service is available but user has not finished yet
    otb.data_validation -> User has finished the selfservice flow and now data validation is in progress
    It's possible that user can't integrate OTB i.e. be on exclusion list. They should never receive the otb.empty_placeholder FF.

    With the combination of above FFs we cover following integration scenarios:
      1. Only PMS integration possible (because OTB already integrated): pricing_assistant.signup.open but no otb.empty_placeholder -> show PmsSsEntryPointBanner
      2. Only OTB integration possible and no PMS integration possible: otb.empty_placeholder but no pricing_assistant.signup.open -> show EmptyStateOtbBanner
      3. Both PMS and OTB integration possible: pricing_assistant.signup.open and otb.empty_placeholder -> show PmsSsEntryPointBanner
      4. No PMS and no OTB integration possible (beta users): no pricing_assistant.signup.open and no pricing_assistant.pms.push_integrated and no otb.empty_placeholder -> show nothing
  */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPricingAssistantPMSIntegrated() {
    return this.access.hasFeature('pricing_assistant.pms.push_integrated');
  }

  // The self service flow is supported, but hasn't been started yet. This flag can be used to show purple banners & OTB purple placeholders
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPricingAssistantPMSSelfServiceAvailable() {
    return this.access.hasFeature('pricing_assistant.signup.open');
  }

  // The self service flow is currently in progress => we need to show a separate waiting state
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPricingAssistantPMSSelfServiceInProgress() {
    return this.access.hasFeature('pricing_assistant.signup.in_progress');
  }

  // Is the big purple upsell banner at the top visible or not: only when the flow is available, but hasn't been started yet (+ also not dismissed by the user)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPricingAssistantPMSSelfServicePrimaryBannerVisible() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.isPricingAssistantPMSSelfServiceAvailable &&
      this.showPricingAssistantPMSSelfServiceBanners
    );
  }

  // A flag to indicate if the more subtle banner variant is visible or not. This variant is shown when the user has dismissed the big purple banner, or when the flow is in progress
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPricingAssistantPMSSelfServiceSecondaryBannerVisible() {
    return (
      (this.isPricingAssistantPMSSelfServiceAvailable &&
        !this.showPricingAssistantPMSSelfServiceBanners) ||
      this.isPricingAssistantPMSSelfServiceInProgress
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get localStorageKey() {
    return `isPricingAssistantPMSSelfServiceVisible_${this.hotelAndMarketService.hotel?.id}`;
  }

  // These banners can be dismissed by the user. This property indicates if that's the case or not.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPricingAssistantPMSSelfServiceBanners() {
    // If bannerVisibilityStates has the key, return the value from there
    if (this.bannerVisibilityStates[this.localStorageKey]) {
      return this.bannerVisibilityStates[this.localStorageKey];
    }

    // Else, fetch the PMS SS upsell banner state from local storage
    const localStoragePmsSsVisibleValue = this.localStorage.getItem(
      this.localStorageKey,
    );

    if (localStoragePmsSsVisibleValue) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return JSON.parse(localStoragePmsSsVisibleValue);
    }

    return true;
  }

  // Show OTB placeholders in purple only when the OTB SS is available, but not in progress or validating
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPricingAssistantPMSSelfServiceOTBPlaceholdersAsUpsellText() {
    return (
      this.access.hasFeature('otb.empty_placeholder') &&
      !this.access.hasFeature('otb.data_validation')
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'pricing-assistant-pms-ss': PricingAssistantPmsSsService;
  }
}
